import { Controller } from "@hotwired/stimulus"

// Barra de atendimento que SUBSTITUI o balão do Duotalk.
//
// Ideia central (robusta): o chat carrega ABERTO (chatOpen:true) e ESCONDIDO atrás da
// barra. Revelar = só mostrar (CSS) — instantâneo e NÃO depende de "abrir no toque"
// (era isso que falhava no mobile: openChat numa iframe ainda não pronta).
//
// Scroll no mobile: enquanto escondido, o CSS força overflow visível e vence a trava
// inline que o Duotalk aplica quando o chat está aberto. Revelado, o Duotalk controla
// (chat fullscreen trava o scroll, como esperado).
//
// Mobile: NÃO auto-abre (só no toque). Desktop: auto-abre UMA vez por visitante
// (flag em localStorage) — nas navegações seguintes só abre por clique na barra.
//
// Classes (em <html> e <body>):
//   chat-launcher-active : nosso launcher montou.
//   chat-revealed        : o chat real está visível (some a barra).
export default class extends Controller {
  static values = {
    autoOpenDelay: { type: Number, default: 6500 },
    minRender: { type: Number, default: 600 }, // contados de quando o iframe ficou pronto
  }

  connect() {
    this.userWantsOpen = false
    this.engaged = false
    this.iframeReadyAt = null
    // Botões "falar pelo chat" das páginas pedem abertura via evento global
    // (window.requestChat) — o launcher conduz a revelação como num clique na barra.
    this.boundExternalOpen = () => this.open()
    window.addEventListener("chat:open", this.boundExternalOpen)
    try {
      this.setClass("chat-launcher-active", true)
      // A animação de entrada da barra toca só na 1ª vez; depois de "vista", reaparecer
      // (ex.: ao fechar o chat) é instantâneo, sem o "fecha e abre".
      this.seenTimer = setTimeout(() => this.setClass("chat-bar-seen", true), 650)
      this.observeWidget()
      // Auto-abre só no desktop (no mobile o dono pediu para não abrir sozinho)
      // e só se ainda não abriu sozinho para este visitante.
      if (window.matchMedia("(min-width: 641px)").matches && !this.autoOpenedBefore()) {
        this.autoTimer = setTimeout(() => this.autoOpen(), this.autoOpenDelayValue)
      }
    } catch (_) {
      this.giveUp() // qualquer erro -> deixa o Duotalk nativo, sem quebrar
    }
  }

  disconnect() {
    window.removeEventListener("chat:open", this.boundExternalOpen)
    clearTimeout(this.autoTimer)
    clearTimeout(this.widgetRetry)
    clearTimeout(this.revealTimer)
    clearTimeout(this.failTimer)
    clearTimeout(this.rewarmTimer)
    clearTimeout(this.seenTimer)
    if (this.observer) this.observer.disconnect()
    this.setClass("chat-launcher-active", false)
    this.setClass("chat-revealed", false)
    this.setClass("chat-bar-seen", false)
  }

  setClass(name, on) {
    [document.documentElement, document.body].forEach((el) => el.classList.toggle(name, on))
  }

  widget() { return document.getElementById("duotalk-chat-widget") }

  open() { // clique na barra
    clearTimeout(this.autoTimer)
    clearTimeout(this.rewarmTimer)
    this.engaged = true
    this.requestReveal()
  }

  autoOpen() { // timer (desktop)
    if (this.engaged) return
    this.markAutoOpened()
    this.engaged = true
    this.requestReveal()
  }

  // Storage indisponível (ex.: modo privado restrito) -> trata como "já abriu",
  // para nunca degradar no caso chato de abrir a cada navegação.
  autoOpenedBefore() {
    try { return localStorage.getItem("vale_chat_auto_opened") === "1" } catch (_) { return true }
  }

  markAutoOpened() {
    try { localStorage.setItem("vale_chat_auto_opened", "1") } catch (_) {}
  }

  requestReveal() {
    this.userWantsOpen = true
    this.armFailsafe()
    this.element.classList.add("is-opening")
    const w = this.widget()
    if (w && w.dataset.open === "true") {
      this.revealWhenReady() // caso comum: já carregou aberto -> revela
    } else if (typeof window.openChat === "function") {
      window.openChat() // raro: não veio aberto -> abre; o sync revela quando abrir
    }
  }

  // Se em ~9s não revelou (CDN fora/erro), devolve o controle nativo ao Duotalk.
  armFailsafe() {
    clearTimeout(this.failTimer)
    this.failTimer = setTimeout(() => {
      if (!document.body.classList.contains("chat-revealed")) this.giveUp()
    }, 9000)
  }

  giveUp() {
    clearTimeout(this.autoTimer)
    clearTimeout(this.widgetRetry)
    clearTimeout(this.revealTimer)
    clearTimeout(this.failTimer)
    clearTimeout(this.rewarmTimer)
    clearTimeout(this.seenTimer)
    if (this.observer) { this.observer.disconnect(); this.observer = null }
    this.element.classList.remove("is-opening")
    this.setClass("chat-launcher-active", false)
    this.setClass("chat-revealed", false)
    this.setClass("chat-bar-seen", false)
  }

  // Revela só depois de o iframe ter tido tempo de renderizar (medido de quando o
  // iframe apareceu — ele carrega ansioso, então no toque já está pronto).
  revealWhenReady() {
    const elapsed = this.iframeReadyAt ? Date.now() - this.iframeReadyAt : 0
    if (elapsed >= this.minRenderValue) {
      this.reveal()
    } else {
      clearTimeout(this.revealTimer)
      this.revealTimer = setTimeout(() => this.reveal(), this.minRenderValue - elapsed)
    }
  }

  reveal() {
    clearTimeout(this.failTimer)
    this.element.classList.remove("is-opening")
    this.setClass("chat-revealed", true)
  }

  observeWidget(attempt = 0) {
    const w = this.widget()
    if (!w) {
      if (attempt >= 20) { this.giveUp(); return } // ~16s sem widget -> Duotalk nativo
      this.widgetRetry = setTimeout(() => this.observeWidget(attempt + 1), 800)
      return
    }
    if (!this.iframeReadyAt) this.iframeReadyAt = Date.now()
    this.sync(w)
    this.observer = new MutationObserver(() => this.sync(w))
    this.observer.observe(w, { attributes: true, attributeFilter: ["data-open"] })
  }

  sync(w) {
    if (w.dataset.open === "true") {
      if (this.userWantsOpen) this.revealWhenReady()
      // escondido + aberto: o CSS já mantém o scroll livre; nada a fazer.
    } else {
      // Fechado (ex.: usuário fechou): volta a barra e reaquece o chat em segundo
      // plano (escondido) para a próxima abertura ser instantânea de novo.
      this.userWantsOpen = false
      clearTimeout(this.revealTimer)
      this.element.classList.remove("is-opening")
      this.setClass("chat-revealed", false)
      this.scheduleRewarm()
    }
  }

  // Reabre o chat ESCONDIDO um instante após o fechamento, para mantê-lo "quente".
  // Como está escondido (display:none) e o CSS mantém o scroll livre, é invisível.
  scheduleRewarm() {
    clearTimeout(this.rewarmTimer)
    this.rewarmTimer = setTimeout(() => {
      const w = this.widget()
      if (!this.userWantsOpen && w && w.dataset.open !== "true" && typeof window.openChat === "function") {
        window.openChat()
      }
    }, 450)
  }
}
